import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth, API } from '../App';
import { LayoutDashboard, Car, Users, BarChart3, Settings, LogOut, Menu, X, Euro, Database, Plug, Fuel, Building2, Wallet, ArrowLeftRight, Truck, History, CreditCard, FileText, ClipboardList, Droplets, CalendarCheck2, Tag, ScrollText, Webhook, BadgeEuro, Route, FlaskConical } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useTranslation } from 'react-i18next';
import LanguageSelector from './LanguageSelector';
import NotificationBell from './NotificationBell';

export default function Sidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { t } = useTranslation();

  // Check if user is admin-driver
  const isAdminDriver = user?.role === 'admin-driver';

  // Map nav paths to section permission keys
  const pathToSection = {
    '/admin': 'dashboard',
    '/admin/rides': 'rides',
    '/admin/past-rides': 'past_rides',
    '/admin/users': 'users',
    '/admin/attendance': 'attendance',
    '/admin/partners': 'partners',
    '/admin/pricing': 'pricing',
    '/admin/vehicles': 'vehicles',
    '/admin/expenses': 'expenses',
    '/admin/petrol': 'petrol',
    '/admin/car-wash': 'car_wash',
    '/admin/financials': 'financials',
    '/admin/debts': 'debts',
    '/admin/daily-vouchers': 'daily_vouchers',
    '/admin/statistics': 'statistics',
    '/admin/partner-reports': 'partner_reports',
    '/admin/backup': 'backup',
    '/admin/integrations': 'integrations',
    '/admin/settings': 'settings'
  };

  const permissions = user?.section_permissions || {};
  const isSuperAdmin = user?.is_super_admin;
  const [hiddenSections, setHiddenSections] = useState([]);

  useEffect(() => {
    axios.get(`${API}/hidden-sections`)
      .then(r => setHiddenSections(r.data.admin || []))
      .catch(() => {});
  }, [location.pathname === '/admin/settings']);

  const navItems = [
    { path: '/admin', label: t('nav.dashboard'), icon: LayoutDashboard },
    { path: '/admin/rides', label: t('nav.rides'), icon: Car },
    { path: '/admin/past-rides', label: 'Past Rides', icon: History },
    { path: '/admin/users', label: t('nav.users'), icon: Users },
    { path: '/admin/attendance', label: 'Attendance', icon: CalendarCheck2 },
    { path: '/admin/partners', label: 'Partners', icon: Building2 },
    { path: '/admin/pricing', label: 'Pricing', icon: Tag },
    { path: '/admin/vehicles', label: 'Vehicles', icon: Truck },
    { path: '/admin/expenses', label: 'Expenses', icon: Euro },
    { path: '/admin/petrol', label: 'Petrol', icon: Fuel },
    { path: '/admin/car-wash', label: 'Car Wash', icon: Droplets },
    { path: '/admin/financials', label: 'Financials', icon: Wallet },
    { path: '/admin/debts', label: 'Company Debts', icon: CreditCard },
    { path: '/admin/daily-vouchers', label: 'Daily Vouchers', icon: FileText },
    { path: '/admin/statistics', label: t('nav.statistics'), icon: BarChart3 },
    { path: '/admin/partner-reports', label: 'Partner Reports', icon: ClipboardList },
    { path: '/admin/backup', label: 'Data Backup', icon: Database },
    { path: '/admin/integrations', label: 'Integrations', icon: Plug },
    { path: '/admin/webhook-events', label: 'Webhook Events', icon: Webhook },
    { path: '/admin/transferz-offers', label: 'Offerte Transferz', icon: BadgeEuro },
    { path: '/admin/transferz-journeys', label: 'Viaggi Transferz', icon: Route },
    { path: '/admin/transferz-test-rides', label: 'Corse Transferz (collaudo)', icon: FlaskConical },
    ...(isSuperAdmin ? [{ path: '/admin/action-log', label: 'Action Log', icon: ScrollText }] : []),
    { path: '/admin/settings', label: t('nav.settings'), icon: Settings }
  ].filter(item => {
    const sectionKey = pathToSection[item.path];
    if (sectionKey && hiddenSections.includes(sectionKey)) return false;
    if (isSuperAdmin) return true;
    return sectionKey ? permissions[sectionKey] !== false : true;
  });

  const isActive = (path) => {
    if (path === '/admin') {
      return location.pathname === '/admin' || location.pathname === '/admin/';
    }
    return location.pathname.startsWith(path);
  };

  const SidebarContent = () => (
    <div className="flex flex-col h-full overflow-hidden">
      <div className="p-6 border-b border-blue-900 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className={collapsed ? 'hidden' : 'flex items-center gap-3'}>
            <img 
              src="/logo.jpg" 
              alt="Outsiders Travel" 
              className="h-12 w-auto object-contain"
            />
            <div>
              <h2 className="text-lg font-bold text-white">{t('common.outsidersTravel')}</h2>
              <p className="text-xs text-blue-300 mt-1">{t('common.adminPanel')}</p>
            </div>
          </div>
          <Button
            onClick={() => setCollapsed(!collapsed)}
            variant="ghost"
            size="icon"
            className="text-blue-300 hover:text-white hover:bg-blue-900 lg:flex hidden"
          >
            <Menu className="h-5 w-5" />
          </Button>
          <Button
            onClick={() => setMobileOpen(false)}
            variant="ghost"
            size="icon"
            className="text-blue-300 hover:text-white hover:bg-blue-900 lg:hidden"
          >
            <X className="h-5 w-5" />
          </Button>
        </div>
      </div>

      <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
        {navItems.map((item) => {
          const Icon = item.icon;
          const active = isActive(item.path);
          return (
            <button
              key={item.path}
              onClick={() => {
                navigate(item.path);
                setMobileOpen(false);
              }}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-sm transition-all ${
                active
                  ? 'bg-sky-500 text-white'
                  : 'text-blue-200 hover:bg-blue-900 hover:text-white'
              }`}
              data-testid={`nav-${item.label.toLowerCase()}`}
            >
              <Icon className="h-5 w-5 flex-shrink-0" />
              {!collapsed && <span className="font-medium">{item.label}</span>}
            </button>
          );
        })}
      </nav>

      <div className="p-4 border-t border-blue-900 flex-shrink-0">
        {!collapsed && (
          <div className="mb-4 flex items-center gap-2">
            <NotificationBell />
            <LanguageSelector />
          </div>
        )}
        
        {/* Switch to Driver View for admin-driver users */}
        {isAdminDriver && !collapsed && (
          <Button
            onClick={() => navigate('/driver')}
            variant="outline"
            className="w-full justify-start mb-3 bg-emerald-600 hover:bg-emerald-700 text-white border-emerald-600"
            data-testid="switch-to-driver-btn"
          >
            <ArrowLeftRight className="h-4 w-4 mr-2" />
            Switch to Driver View
          </Button>
        )}
        {isAdminDriver && collapsed && (
          <Button
            onClick={() => navigate('/driver')}
            variant="outline"
            size="icon"
            className="w-full mb-3 bg-emerald-600 hover:bg-emerald-700 text-white border-emerald-600"
            title="Switch to Driver View"
            data-testid="switch-to-driver-btn-collapsed"
          >
            <ArrowLeftRight className="h-4 w-4" />
          </Button>
        )}
        
        <div className={`mb-4 ${collapsed ? 'hidden' : 'block'}`}>
          <p className="text-xs text-blue-300 mb-1">{t('common.loggedInAs')}</p>
          <p className="text-sm text-white font-medium">{user?.name}</p>
          <p className="text-xs text-blue-300">{user?.email}</p>
          {isAdminDriver && (
            <span className="inline-block mt-1 px-2 py-0.5 bg-purple-600 text-white text-xs rounded-full">
              Admin-Driver
            </span>
          )}
        </div>
        <Button
          onClick={logout}
          variant="ghost"
          className="w-full justify-start text-blue-200 hover:text-white hover:bg-blue-900"
          data-testid="sidebar-logout"
        >
          <LogOut className="h-5 w-5 mr-3" />
          {!collapsed && <span>{t('common.logout')}</span>}
        </Button>
      </div>
    </div>
  );

  return (
    <>
      {/* Desktop Sidebar - Hidden on mobile (mobile uses MobileBottomNav instead) */}
      <aside
        className={`${
          collapsed ? 'w-20' : 'w-64'
        } bg-black text-blue-200 flex-shrink-0 hidden lg:block transition-all duration-300`}
      >
        <div className="noise-overlay"></div>
        <div className="relative z-10 h-full">
          <SidebarContent />
        </div>
      </aside>
    </>
  );
}